/**
 * Documents on a resource-directory card — API client (the org page's
 * Documents section; server: api/src/functions/providerAttachments.ts).
 *
 * A standalone client in the src/lib/documents.ts idiom: the upload path
 * needs the direct-to-Blob fetch plumbing that lives outside api.ts's
 * wrapper. Native fetch, ApiError normalization, redirect:'manual' on
 * writes for the auth-expiry signal.
 *
 *   POST   /api/t/{slug}/v1/providers/{id}/attachments                     handshake step 1
 *   (PUT the bytes to the returned single-use URL)                        handshake step 2
 *   POST   /api/t/{slug}/v1/provider-attachments/{attachmentId}/complete   handshake step 3
 *   GET    /api/t/{slug}/v1/providers/{id}/attachments                     the list (active only)
 *   POST   /api/t/{slug}/v1/provider-attachments/{attachmentId}/download   the short-lived read grant
 *   DELETE /api/t/{slug}/v1/provider-attachments/{attachmentId}            hard delete (row + stored file)
 *
 * The file policy (the four accepted types, the 25 MB ceiling, the
 * previewable subset) is the card-attachments policy — one substrate,
 * one DB CHECK — so those constants are imported, never re-declared.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import { putFileBytes, sha256HexOf } from './blobUpload';
import {
  ATTACHMENT_MAX_BYTES,
  resolveAttachmentContentType,
  type AttachmentDisposition,
} from './workItemAttachments';

export {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  isAttachmentPreviewable,
  resolveAttachmentContentType,
  type AttachmentDisposition,
} from './workItemAttachments';

// ---------------------------------------------------------------------
// Wire shapes (mirror api/_shared/repos/providerAttachments.ts)
// ---------------------------------------------------------------------

export interface ProviderAttachmentWire {
  id: string;
  provider_id: string;
  display_name: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  /** Resolved server-side — a raw uuid never reaches the screen. */
  uploaded_by_display_name: string | null;
  /** Decides whether the row's Delete shows for the current user; never rendered. */
  uploaded_by_user_id: string;
}

export interface ProviderAttachmentsListResponse {
  rows: ProviderAttachmentWire[];
}

export interface CreateProviderAttachmentResponse {
  attachment_id: string;
  upload_url: string;
  upload_expires_at: string;
}

export interface ProviderAttachmentDownloadGrantResponse {
  attachment_id: string;
  provider_id: string;
  download_url: string;
  download_expires_at: string;
  display_name: string;
  content_type: string;
  /** The EFFECTIVE (server-coerced) disposition — the client never decides. */
  disposition: AttachmentDisposition;
}

export interface DeleteProviderAttachmentResponse {
  attachment_id: string;
  deleted: true;
}

// ---------------------------------------------------------------------
// Fetch plumbing (documents.ts / notes.ts semantics)
// ---------------------------------------------------------------------

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return await res.text().catch(() => null);
  }
}

async function get<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(tenantUrl(slug, path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `GET ${path} reached HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function post<T>(slug: string, path: string, body: Record<string, unknown>): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `POST ${path} reached HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function del<T>(slug: string, path: string): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `DELETE ${path} reached HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------
// The API surface
// ---------------------------------------------------------------------

export function providerAttachmentsKey(slug: string, providerId: string): readonly string[] {
  return ['provider-attachments', slug, providerId] as const;
}

export const providerAttachmentsApi = {
  list(slug: string, providerId: string, signal?: AbortSignal): Promise<ProviderAttachmentsListResponse> {
    return get(slug, `/providers/${encodeURIComponent(providerId)}/attachments`, signal);
  },

  /**
   * The whole three-step handshake behind ONE mutation: declare (pending
   * row + single-blob write URL) then PUT the bytes then complete
   * (verify + flip active). Throws ApiError at whichever step failed; a
   * failed step leaves at most a harmless pending row server-side, so
   * retry means running it again.
   */
  async attach(slug: string, providerId: string, file: File): Promise<{ attachment_id: string }> {
    const contentType = resolveAttachmentContentType(file);
    if (contentType === null) {
      throw new ApiError(
        400,
        { error: 'invalid_body', field: 'content_type' },
        'file type not accepted for a directory document',
      );
    }
    if (file.size <= 0 || file.size > ATTACHMENT_MAX_BYTES) {
      throw new ApiError(400, { error: 'invalid_body', field: 'size_bytes' }, 'file too large');
    }

    const declared = await post<CreateProviderAttachmentResponse>(
      slug,
      `/providers/${encodeURIComponent(providerId)}/attachments`,
      { display_name: file.name, content_type: contentType, size_bytes: file.size },
    );

    await putFileBytes(declared.upload_url, file, contentType);

    const sha256_hex = await sha256HexOf(file);
    await post(slug, `/provider-attachments/${encodeURIComponent(declared.attachment_id)}/complete`, {
      sha256_hex,
    });

    return { attachment_id: declared.attachment_id };
  },

  /**
   * The short-lived single-file read grant. 'inline' is honored only for
   * previewable types — the server coerces anything else (docx) back to
   * a plain download against the STORED content type.
   */
  downloadGrant(
    slug: string,
    attachmentId: string,
    disposition: AttachmentDisposition = 'attachment',
  ): Promise<ProviderAttachmentDownloadGrantResponse> {
    return post(slug, `/provider-attachments/${encodeURIComponent(attachmentId)}/download`, {
      disposition,
    });
  },

  /** Hard delete: the row, then the stored file. Permanent. */
  remove(slug: string, attachmentId: string): Promise<DeleteProviderAttachmentResponse> {
    return del(slug, `/provider-attachments/${encodeURIComponent(attachmentId)}`);
  },
};
